/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { MaskedInputForm } from '../../inputs/forms/MaskedInputForm';
import { PasswordInputForm } from '../../inputs/forms/PasswordInputForm';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { getFormattedLabel } from '../../../utils/FormatFunctions';
import { mapMultiSelectToRelation } from '../../../utils/formMappers';
import { PASSWORD_PATTERN, PASSWORD_POLICY_MESSAGE } from '../../../utils/passwordValidation';
import { isValidOptionalCpf } from '../../../utils/cpfValidation';

interface UserFormProps {
  defaultValues?: any;
  fixedGroup?: {
    id: number;
    name: string;
  };
  returnPage?: string;
  entityLabel?: string;
  backLabel?: string;
  requireCellPhone?: boolean;
  requireInstitutions?: boolean;
}

interface FormInputsProps {
  name: string;
  value: string;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function UserForm({
  defaultValues = {},
  fixedGroup,
  returnPage = 'users',
  entityLabel = 'Usuário',
  backLabel = 'Usuários',
  requireCellPhone = false,
  requireInstitutions = false,
}: UserFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/users';
  const { requestApi: reqApiUsers } = useFetch(baseUrl);
  const {
    requestApi: reqApiGroups,
    data: dataGroups,
    loading: loadingGroups,
  } = useFetch('/v1/admin/groups');
  const { data: optionsGroups, pagination: paginationGroups } = getFetchData(dataGroups);
  const {
    requestApi: reqApiInstitutions,
    data: dataInstitutions,
    loading: loadingInstitutions,
  } = useFetch('/v1/admin/institutions');
  const { data: optionsInstitutions, pagination: paginationInstitutions } =
    getFetchData(dataInstitutions);
  const {
    requestApi: reqApiSecretaries,
    data: dataSecretaries,
    loading: loadingSecretaries,
  } = useFetch('/v1/admin/secretaries');
  const { data: optionsSecretaries, pagination: paginationSecretaries } =
    getFetchData(dataSecretaries);
  const { itemId } = useParams();
  const navigate = useNavigate();
  const assignedGroup = fixedGroup || defaultValues?.group || null;
  const [images, setImages] = useState<any>('');
  const [uploading, setUploading] = useState<any>(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState<boolean>(true);
  const [selectedGroup, setSelectedGroup] = useState<any>(assignedGroup);
  const [, setSelectedInstitutions] = useState<any[]>([]);
  const [, setSelectedSecretaries] = useState<any[]>([]);

  const optionsGenre = [
    {
      label: 'Masculino',
      value: 'M',
    },
    {
      label: 'Feminino',
      value: 'F',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};
  const normalizePassword = (value: any) => (typeof value === 'string' ? value.trim() : value);

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do usuário',
      label: 'Nome',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .min(3, 'Nome deve ter pelo menos 3 caracteres'),
    },
    {
      name: 'email',
      value: defaultValues.email ?? '',
      placeholder: 'usuario@email.com',
      label: 'Email',
      yup: Yup.string().email('Email inválido').required('Este campo é obrigatório!'),
    },
    {
      name: 'cell_phone',
      value: defaultValues.cell_phone ?? '',
      placeholder: '(99) 99999-9999',
      label: 'Telefone',
      yup: requireCellPhone ? Yup.string().required('Este campo é obrigatório!') : Yup.string(),
    },
    {
      name: 'taxpayer',
      value: defaultValues.taxpayer ?? '',
      placeholder: '999.999.999-99',
      label: 'CPF',
      yup: Yup.string().test('cpf', 'CPF inválido', isValidOptionalCpf),
    },
    {
      name: 'genre',
      value: defaultValues?.genre
        ? optionsGenre.find((item) => item.value === defaultValues?.genre)
        : null,
      placeholder: 'Gênero do usuário',
      label: 'Gênero',
      yup: Yup.mixed(),
    },
    {
      name: 'group_id',
      value: assignedGroup ? getFormattedLabel(assignedGroup) : null,
      placeholder: 'Grupo do usuário',
      label: 'Grupo',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'password',
      value: '',
      placeholder: 'Senha',
      label: 'Senha',
      yup: Yup.string()
        .transform(normalizePassword)
        .test(
          'password-required',
          'Este campo é obrigatório!',
          (value) => !!defaultValues.name || useDefaultPassword || !!value
        )
        .test(
          'password-policy',
          PASSWORD_POLICY_MESSAGE,
          (value) => !value || PASSWORD_PATTERN.test(value)
        ),
    },
    {
      name: 'confirm_password',
      value: '',
      placeholder: 'Digite a senha novamente',
      label: 'Confirmação de senha',
      yup: Yup.string()
        .transform(normalizePassword)
        .test(
          'password-confirmation-required',
          'Este campo é obrigatório!',
          (value) => !!defaultValues.name || useDefaultPassword || !!value
        )
        .test(
          'password-confirmation',
          'A confirmação da senha não confere',
          (value, context) => !context.parent.password || value === context.parent.password
        ),
    },
    {
      name: 'image',
      value: defaultValues.image?.original ?? null,
      placeholder: '',
      label: 'Foto de perfil',
      yup: Yup.mixed().nullable(),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  yupschema.use_default_password_switch = Yup.boolean();
  defaultValuesSchema.use_default_password_switch = true;

  // Adicionar campos condicionais ao schema
  yupschema.institutions = requireInstitutions
    ? Yup.array().min(1, 'Selecione pelo menos uma instituição').required()
    : Yup.mixed();
  yupschema.secretaries = Yup.mixed();
  yupschema.secretary_id = Yup.mixed();
  yupschema.institution_id = Yup.mixed();

  defaultValuesSchema.institutions =
    defaultValues?.institutions?.map((inst: any) => getFormattedLabel(inst)) ?? [];
  defaultValuesSchema.secretaries =
    defaultValues?.secretaries?.map((sec: any) => getFormattedLabel(sec)) ?? [];
  defaultValuesSchema.secretary_id = defaultValues?.secretary
    ? getFormattedLabel(defaultValues.secretary)
    : null;
  defaultValuesSchema.institution_id = defaultValues?.institution
    ? getFormattedLabel(defaultValues.institution)
    : null;

  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const email = formInputs.find((field) => field.name === 'email');
  const cellphone = formInputs.find((field) => field.name === 'cell_phone');
  const taxpayer = formInputs.find((field) => field.name === 'taxpayer');
  const genre = formInputs.find((field) => field.name === 'genre');
  const groupId = formInputs.find((field) => field.name === 'group_id');
  // const password = formInputs.find(field => field.name === 'password');
  // const confirmPassword = formInputs.find(field => field.name === 'confirm_password');
  const image = formInputs.find((field) => field.name === 'image');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params: any = {
      ...values,
      genre: values.genre?.value ?? null,
      password: (() => {
        if (defaultValues.name) return values.password?.trim() || _;
        if (useDefaultPassword) return '123456';
        return values.password?.trim();
      })(),
      use_default_password: !defaultValues.name && useDefaultPassword,
      group_id: fixedGroup?.id ?? values.group_id.value,
      confirm_password: _,
      image: _,
    };

    // Adicionar vínculos baseados no grupo
    if (isTutorOrTeacher) {
      params.institutions = mapMultiSelectToRelation(values.institutions);
    }

    if (isResponsible) {
      params.secretaries = mapMultiSelectToRelation(values.secretaries);
      params.institutions = mapMultiSelectToRelation(values.institutions);
    }

    if (isSecretaryResponsible) {
      if (values.secretary_id) {
        const mappedSecretary = mapMultiSelectToRelation(values.secretary_id);
        if (mappedSecretary.length) {
          params.secretaries = mappedSecretary;
        }
      }
      if (values.institutions) {
        const mappedInstitutions = mapMultiSelectToRelation(values.institutions);
        if (mappedInstitutions.length) {
          params.institutions = mappedInstitutions;
        }
      }
    }

    if (isInstitutionResponsible) {
      if (values.secretary_id) {
        const mappedSecretary = mapMultiSelectToRelation(values.secretary_id);
        if (mappedSecretary.length) {
          params.secretaries = mappedSecretary;
        }
      }
      if (values.institution_id) {
        const mappedInstitution = mapMultiSelectToRelation(values.institution_id);
        if (mappedInstitution.length) {
          params.institutions = mappedInstitution;
        }
      }
    }

    // Remover campos auxiliares que não devem ser enviados
    delete params.secretary_id;
    delete params.institution_id;

    const { status: resStatus, data } = await reqApiUsers(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    const created = resStatus === 200 || resStatus === 201;
    if (created && images) {
      setUploading(true);
      const apiUrlId = itemId ?? data?.data?.id;
      await reqApiUsers('put', { file: images.toString() }, `${baseUrl}/${apiUrlId}/images`);
      setUploading(false);
    }
    if (created) navigate(`/${returnPage}`);
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
      setSelectedGroup(fixedGroup || defaultValues?.group || null);

      // Carregar instituições vinculadas
      if (defaultValues?.institutions) {
        setSelectedInstitutions(
          defaultValues.institutions.map((inst: any) => ({
            id: inst.id,
            name: inst.name,
          }))
        );
      }

      // Carregar secretarias vinculadas
      if (defaultValues?.secretaries) {
        setSelectedSecretaries(
          defaultValues.secretaries.map((sec: any) => ({
            id: sec.id,
            name: sec.name,
          }))
        );
      }
    }
  }, [defaultValues]);

  // Monitora mudança no grupo selecionado
  const watchGroup = watch('group_id');
  useEffect(() => {
    if (fixedGroup) {
      setSelectedGroup(fixedGroup);
    } else if (watchGroup?.value) {
      const group = optionsGroups?.find((g: any) => g.id === watchGroup.value);
      setSelectedGroup(group || null);
    }
  }, [watchGroup, optionsGroups, fixedGroup]);

  // Verifica se o grupo é Admin ou Sistema
  const isAdminOrSystem =
    selectedGroup?.name === 'Administrador' || selectedGroup?.name === 'Sistema';

  // Verifica se o grupo é Tutor ou Professor
  const isTutorOrTeacher = selectedGroup?.name === 'Tutor' || selectedGroup?.name === 'Professor';

  // Verifica se é Responsável
  const isResponsible = selectedGroup?.name === 'Responsável';

  // Verifica se é Responsável da secretaria
  const isSecretaryResponsible = selectedGroup?.name === 'Responsável da secretaria';

  // Verifica se é Responsável da instituição
  const isInstitutionResponsible = selectedGroup?.name === 'Responsável da instituição';

  const handleDefaultPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setUseDefaultPassword(checked);
    if (checked) {
      setValue('password', '');
      setValue('confirm_password', '');
      clearErrors(['password', 'confirm_password']);
    }
  };

  const isEdit = Boolean(defaultValues.name);
  const showLinks =
    !isAdminOrSystem &&
    (isTutorOrTeacher || isResponsible || isSecretaryResponsible || isInstitutionResponsible);

  return (
    <FormShell
      title={`${isEdit ? 'Editar' : 'Criar'} ${entityLabel}`}
      backTo={`/${returnPage}`}
      backLabel={backLabel}
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações básicas">
          <ImageInputForm
            variant="avatar"
            avatarName={watch('name')}
            defaultVal={defaultValues?.image}
            setImages={setImages}
            setIsUpping={setUploading}
            name={image?.name}
            label={image?.label}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <InputForm
              name={email?.name}
              label={email?.label}
              placeholder={email?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </FormRow>
          <FormRow>
            <MaskedInputForm
              name={cellphone?.name}
              label={cellphone?.label}
              placeholder={cellphone?.placeholder}
              isRequired={requireCellPhone}
              control={control}
              errors={errors}
              mask="(99) 99999-9999"
            />
            <MaskedInputForm
              name={taxpayer?.name}
              label={taxpayer?.label}
              placeholder={taxpayer?.placeholder}
              control={control}
              errors={errors}
              mask="999.999.999-99"
            />
            <SelectInputForm
              name={genre?.name}
              label={genre?.label}
              options={optionsGenre}
              placeholder={genre?.placeholder}
              control={control}
              errors={errors}
            />
          </FormRow>
        </FormSection>

        <FormSection title="Acesso">
          <FormRow gridTemplateColumns={{ base: '1fr', md: '2fr 1fr' }}>
            <SelectInputForm
              name={groupId?.name}
              label={groupId?.label}
              options={[...(optionsGroups?.map((item) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingGroups}
              pagination={paginationGroups}
              getOptions={(filters) => reqApiGroups('get', { params: { ...filters } })}
              isRequired
              isDisabled={!!fixedGroup}
              isClearable={!fixedGroup}
              placeholder={groupId?.placeholder}
              control={control}
              errors={errors}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </FormRow>
        </FormSection>

        <FormSection
          title="Segurança"
          description={isEdit ? 'Deixe os campos vazios para manter a senha atual.' : undefined}
        >
          {!isEdit && (
            <SwitchInputForm
              name="use_default_password_switch"
              label="Senha inicial"
              subLabel="Usar senha padrão (123456)"
              onChange={handleDefaultPasswordChange}
              control={control}
              errors={errors}
            />
          )}
          {(isEdit || !useDefaultPassword) && (
            <FormRow>
              <PasswordInputForm
                name="password"
                label={isEdit ? 'Nova senha' : 'Senha'}
                placeholder="Digite a senha"
                isRequired={!isEdit}
                register={register}
                errors={errors}
                autoComplete="new-password"
              />
              <PasswordInputForm
                name="confirm_password"
                label={isEdit ? 'Confirmar nova senha' : 'Confirmação de senha'}
                placeholder="Digite a senha novamente"
                isRequired={!isEdit}
                register={register}
                errors={errors}
                autoComplete="new-password"
              />
            </FormRow>
          )}
        </FormSection>

        {showLinks && (
          <FormSection
            title="Vínculos"
            description="Defina a quais secretarias e instituições este usuário tem acesso."
          >
            {isTutorOrTeacher && (
              <SelectInputForm
                name="institutions"
                label="Instituições vinculadas"
                options={[...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? [])]}
                isLoading={loadingInstitutions}
                isMulti
                isRequired={requireInstitutions}
                pagination={paginationInstitutions}
                getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
                placeholder="Selecione as instituições"
                control={control}
                errors={errors}
              />
            )}

            {isResponsible && (
              <FormRow>
                <SelectInputForm
                  name="secretaries"
                  label="Secretarias vinculadas"
                  options={[...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? [])]}
                  isLoading={loadingSecretaries}
                  isMulti
                  pagination={paginationSecretaries}
                  getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                  placeholder="Selecione as secretarias"
                  control={control}
                  errors={errors}
                />
                <SelectInputForm
                  name="institutions"
                  label="Instituições vinculadas"
                  options={[...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? [])]}
                  isLoading={loadingInstitutions}
                  isMulti
                  pagination={paginationInstitutions}
                  getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
                  placeholder="Selecione as instituições"
                  control={control}
                  errors={errors}
                />
              </FormRow>
            )}

            {isSecretaryResponsible && (
              <FormRow>
                <SelectInputForm
                  name="secretary_id"
                  label="Secretaria"
                  options={[...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? [])]}
                  isLoading={loadingSecretaries}
                  pagination={paginationSecretaries}
                  getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                  placeholder="Selecione a secretaria"
                  control={control}
                  errors={errors}
                />
                <SelectInputForm
                  name="institutions"
                  label="Instituições da secretaria"
                  options={[...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? [])]}
                  isLoading={loadingInstitutions}
                  isMulti
                  pagination={paginationInstitutions}
                  getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
                  placeholder="Selecione as instituições"
                  control={control}
                  errors={errors}
                />
              </FormRow>
            )}

            {isInstitutionResponsible && (
              <FormRow>
                <SelectInputForm
                  name="secretary_id"
                  label="Secretaria vinculada"
                  options={[...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? [])]}
                  isLoading={loadingSecretaries}
                  pagination={paginationSecretaries}
                  getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                  placeholder="Selecione a secretaria"
                  control={control}
                  errors={errors}
                />
                <SelectInputForm
                  name="institution_id"
                  label="Instituição"
                  options={[...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? [])]}
                  isLoading={loadingInstitutions}
                  pagination={paginationInstitutions}
                  getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
                  isRequired
                  placeholder="Selecione a instituição"
                  control={control}
                  errors={errors}
                />
              </FormRow>
            )}
          </FormSection>
        )}

        <FormActions cancelTo={`/${returnPage}`} isLoading={isSubmitting || uploading} />
      </Box>
    </FormShell>
  );
}
